import type { AssertionEvaluationOutcome } from '@chatsdet/types';

/**
 * Structured browser ownership signal. Authored code can throw arbitrary
 * message text, so callers evaluating authored code must use this probe (or an
 * explicit caller abort) instead of inferring teardown from an exception
 * message.
 */
export function hasStructuredPageTeardown(page: unknown): boolean {
  if (typeof page !== 'object' || page === null) return false;
  const probe = page as {
    isClosed?: () => boolean;
    context?: () => {
      browser?: () => {
        isConnected?: () => boolean;
      } | null;
    };
  };
  try {
    if (probe.isClosed?.()) return true;
    const browser = probe.context?.().browser?.();
    return browser?.isConnected ? !browser.isConnected() : false;
  } catch {
    // A page that can no longer report its owning context is unusable.
    return true;
  }
}

/**
 * Classify run-code execution exclusively from structured ownership signals.
 *
 * Error text is deliberately absent from this API: authored code controls its
 * exception messages, so accepting an error here would let it impersonate
 * browser teardown. Requiring both booleans makes every caller prove where an
 * infrastructure outcome came from.
 */
export function classifyRunCodeOutcomeFromSignals(params: {
  ok: boolean;
  aborted: boolean;
  pageTornDown: boolean;
}): AssertionEvaluationOutcome {
  if (params.aborted || params.pageTornDown) return 'infrastructure_error';
  if (params.ok) return 'passed';
  return 'authored_error';
}
